/**
 * Derive a "hover" version of a brand hex by bumping saturation and dropping
 * lightness in HSL space. Tried `color-mix(..., black)` (looks muddy — grey,
 * not richer) and `hsl(from var(--x) …)` relative-color syntax (resolved as
 * `transparent` in some engines when the source is a `var()`), so we compute
 * it eagerly here and emit a concrete `#rrggbb` for the consumer to set on
 * a CSS custom property.
 *
 * Defaults — saturation +14pp, lightness -12pp — match the "more saturated,
 * a bit darker" feel asked for by design (vs. the desaturated wash that
 * mix-with-black produces). Earlier passes tried -14pp lightness (too dark
 * on the magenta brand stop) and -8pp (too close to the base, hover read
 * as a no-op); -12pp lands between the two and gives a clearly distinct
 * pressed cue. Saturation carries the "richer" half of the feel — it's
 * what stops the hover from looking like a flat grey wash. Both deltas
 * clamp to the [0, 100] range so a brand colour at either extreme degrades
 * gracefully (e.g. white doesn't wrap around).
 *
 * Accepts `#rgb` and `#rrggbb` (with or without the leading `#`). Any other
 * input is handled gracefully and never throws: a usable non-hex string
 * (`rgb(...)`, named colours, `var(...)` strings) is returned unchanged so the
 * call is safe to make with whatever the theme config holds, and anything that
 * isn't a usable string at all (null / undefined / number / object — e.g. a
 * malformed event-config colour) falls back to `FALLBACK_HEX`. This matters
 * because it runs during bootstrap from an APP_INITIALIZER: a throw here would
 * abort bootstrap and leave a white screen. Use only for hover/pressed states
 * on brand surfaces; non-brand neutrals have their own hover tokens.
 *
 * Achromatic (grey / white / near-neutral) inputs, where the three channels
 * are within `NEUTRAL_CHROMA` of each other, are darkened/lightened along
 * lightness only — no saturation is added — so a grey stays grey and
 * white/near-white doesn't pick up a pink (hue-0) tint from the sat bump.
 */
const FALLBACK_HEX = '#000000';

// Max spread between the R, G and B channels (in 0–1 space, ≈4/255) for an
// input to count as neutral. 4 levels out of 255 catches true greys and the
// slightly-off greys that colour tooling emits, while staying far below the
// chroma of any real brand stop so chromatic hover results are unchanged.
const NEUTRAL_CHROMA = 4 / 255;

export const deriveHoverHex = (
  hex: string,
  satDeltaPp = 14,
  lightDeltaPp = -12
): string => {
  if (typeof hex !== 'string') return FALLBACK_HEX;
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const raw =
    m[1].length === 3
      ? m[1]
          .split('')
          .map((c) => c + c)
          .join('')
      : m[1];
  const r = parseInt(raw.slice(0, 2), 16) / 255;
  const g = parseInt(raw.slice(2, 4), 16) / 255;
  const b = parseInt(raw.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l0 = (max + min) / 2;
  const isAchromatic = max - min <= NEUTRAL_CHROMA;
  let s0 = 0;
  let h = 0;
  if (!isAchromatic) {
    const d = max - min;
    s0 = l0 > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h /= 6;
  }

  const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));
  // Neutrals keep s = 0 so the hover shifts on lightness only (no hue tint).
  const s = isAchromatic ? 0 : clamp01(s0 + satDeltaPp / 100);
  const l = clamp01(l0 + lightDeltaPp / 100);

  const hue2rgb = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  let r2: number;
  let g2: number;
  let b2: number;
  if (s === 0) {
    r2 = g2 = b2 = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r2 = hue2rgb(p, q, h + 1 / 3);
    g2 = hue2rgb(p, q, h);
    b2 = hue2rgb(p, q, h - 1 / 3);
  }
  const toHex = (v: number): string =>
    Math.round(v * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r2)}${toHex(g2)}${toHex(b2)}`;
};
